import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const TIMELINE = [
  { date: 'Oct 2024', label: 'CRA entered into force', done: true },
  { date: 'Sep 2026', label: 'Notification bodies ready', done: false },
  { date: 'Dec 2027', label: 'Full enforcement begins', done: false },
];

const REQUIREMENTS = [
  {
    article: 'Art. 13',
    title: 'Obligations of manufacturers',
    items: [
      { req: 'Perform due diligence on open-source components', depguard: 'SBOM scan + CVE analysis on every dependency', covered: true },
      { req: 'Ensure components are free of known vulnerabilities at release', depguard: 'Policy gate blocks release if critical CVE detected', covered: true },
      { req: 'Publish and maintain a Software Bill of Materials', depguard: 'SBOM upload, storage, versioning, and diff tracking', covered: true },
      { req: 'Systematic vulnerability handling process', depguard: 'Remediation tracking with owner, due date, and audit trail', covered: true },
    ],
  },
  {
    article: 'Art. 14',
    title: 'Vulnerability reporting obligations',
    items: [
      { req: 'Report actively exploited vulnerabilities within 24 hours', depguard: 'Webhook alerts on new CRITICAL CVEs with CVSS ≥ 9.0', covered: true },
      { req: 'Report severe incidents within 72 hours', depguard: 'Alert engine + Slack/Teams/email notifications', covered: true },
      { req: 'Notify affected users without undue delay', depguard: 'VEX statement workflow to communicate status', covered: true },
    ],
  },
  {
    article: 'Art. 20',
    title: 'Technical documentation requirements',
    items: [
      { req: 'SBOM with PURL, supplier, hash, version for every component', depguard: 'SBOM quality checker validates all 5 mandatory fields', covered: true },
      { req: 'Dependency relationships documented', depguard: 'Dependency graph from CycloneDX dependsOn data', covered: true },
      { req: 'Retain technical documentation for 10 years', depguard: 'SBOM history with immutable audit log (Enterprise)', covered: 'partial' },
      { req: 'Downloadable audit document for regulators', depguard: 'One-click CRA compliance report with compliance %, gaps, and remediation', covered: true },
    ],
  },
];

const SBOM_FIELDS = [
  { field: 'Package name', required: true,  example: 'log4j-core' },
  { field: 'Package version', required: true,  example: '2.14.1' },
  { field: 'Supplier / author', required: true,  example: 'org.apache.logging.log4j' },
  { field: 'PURL', required: true,  example: 'pkg:maven/org.apache.logging.log4j/log4j-core@2.14.1' },
  { field: 'Cryptographic hash', required: true,  example: 'sha256:abc123...' },
  { field: 'Dependency relationships', required: true,  example: 'dependsOn: [spring-core@5.3.9]' },
  { field: 'Timestamp', required: true,  example: '2024-11-01T09:00:00Z' },
  { field: 'License', required: false, example: 'Apache-2.0' },
  { field: 'Component description', required: false, example: 'Apache Log4j logging library' },
];

function CoveredChip({ covered }) {
  if (covered === true)      return <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'rgba(16,185,129,.1)',color:'#065f46',border:'1px solid rgba(16,185,129,.25)'}}>✓ Covered</span>;
  if (covered === 'partial') return <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'rgba(245,158,11,.1)',color:'#78350f',border:'1px solid rgba(245,158,11,.25)'}}>~ Partial</span>;
  return <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'rgba(239,68,68,.1)',color:'#991b1b',border:'1px solid rgba(239,68,68,.25)'}}>✗ Gap</span>;
}

export default function WebsiteEuCraPage() {
  const [open, setOpen] = useState(0);

  return (
    <div className="dgw-page-wrap">

      {/* Hero */}
      <section className="dgw-hero" style={{textAlign:'center'}}>
        <div className="dgw-hero-inner" style={{maxWidth:880}}>
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            EU Cyber Resilience Act
          </div>
          <h1>EU CRA compliance,<br/>automated — not assembled.</h1>
          <p className="dgw-hero-lead" style={{maxWidth:640, margin:'0 auto 32px'}}>
            Penalties under the EU Cyber Resilience Act reach €15 million or 2.5% of global turnover.
            DepGuard automates the evidence collection, validation, and reporting so your team
            can focus on fixing vulnerabilities instead of filling spreadsheets.
          </p>
          <div style={{
            display:'inline-flex', gap:24, padding:'16px 32px', borderRadius:12,
            background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.25)',
            marginBottom:40, flexWrap:'wrap', justifyContent:'center',
          }}>
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:22, fontWeight:900, color:'#ef4444'}}>€15M</div>
              <div style={{fontSize:11, color:'#94a3b8'}}>max penalty</div>
            </div>
            <div style={{width:1, background:'rgba(255,255,255,.1)'}} />
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:22, fontWeight:900, color:'#ef4444'}}>2.5%</div>
              <div style={{fontSize:11, color:'#94a3b8'}}>global turnover</div>
            </div>
            <div style={{width:1, background:'rgba(255,255,255,.1)'}} />
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:22, fontWeight:900, color:'#f59e0b'}}>Dec 2027</div>
              <div style={{fontSize:11, color:'#94a3b8'}}>full enforcement</div>
            </div>
          </div>
          <div className="dgw-hero-actions" style={{justifyContent:'center'}}>
            <Link to="/trial" className="dgw-btn dgw-btn-primary dgw-btn-lg">Generate CRA report →</Link>
            <Link to="/website/whitepaper" className="dgw-btn dgw-btn-ghost dgw-btn-lg">Read the whitepaper</Link>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:700, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag">Timeline</span>
            <h2>When does CRA apply to you?</h2>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:0}}>
            {TIMELINE.map((t, i) => (
              <div key={t.date} style={{display:'grid', gridTemplateColumns:'100px 1fr', gap:20, paddingBottom: i < TIMELINE.length - 1 ? 32 : 0, position:'relative'}}>
                {i < TIMELINE.length - 1 && <div style={{position:'absolute', left:49, top:36, bottom:0, width:2, background:t.done ? '#6366f1' : '#e2e8f0'}} />}
                <div style={{
                  width:40, height:40, borderRadius:'50%', flexShrink:0,
                  background: t.done ? '#6366f1' : '#f1f5f9',
                  border: `2px solid ${t.done ? '#6366f1' : '#e2e8f0'}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:16, zIndex:1,
                }}>{t.done ? '✓' : '○'}</div>
                <div style={{paddingTop:8}}>
                  <div style={{fontSize:12, fontWeight:700, color: t.done ? '#6366f1' : '#94a3b8', marginBottom:4}}>{t.date}</div>
                  <div style={{fontSize:15, fontWeight:600, color:'#0f172a'}}>{t.label}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{
            marginTop:32, padding:'20px 24px', borderRadius:12,
            background:'rgba(245,158,11,.08)', border:'1px solid rgba(245,158,11,.25)',
          }}>
            <strong style={{color:'#78350f', fontSize:14}}>⚠ Don't wait for the 2027 deadline</strong>
            <p style={{color:'#92400e', fontSize:14, margin:'8px 0 0', lineHeight:1.6}}>
              Article 20 requires technical documentation to be maintained throughout the product lifecycle,
              not assembled retroactively the week before an audit. Three years of SBOM history looks
              very different to a regulator than three weeks.
            </p>
          </div>
        </div>
      </section>

      {/* Article-by-article */}
      <section className="dgw-section" style={{background:'#f8fafc'}}>
        <div style={{maxWidth:1000, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:48}}>
            <span className="dgw-section-tag">Article mapping</span>
            <h2>What CRA requires. What DepGuard covers.</h2>
            <p className="dgw-section-lead">Article-by-article breakdown of the open-source supply chain obligations.</p>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:4}}>
            {REQUIREMENTS.map((section, si) => {
              const isOpen = open === si;
              return (
                <div key={section.article} style={{borderRadius:14, overflow:'hidden', border:`1px solid ${isOpen ? 'rgba(99,102,241,.3)' : '#e2e8f0'}`, background:'#fff'}}>
                  <button onClick={() => setOpen(isOpen ? -1 : si)} style={{
                    width:'100%', display:'flex', justifyContent:'space-between',
                    alignItems:'center', gap:16, padding:'20px 24px',
                    background:'none', border:'none', cursor:'pointer', textAlign:'left',
                  }}>
                    <div style={{display:'flex', alignItems:'center', gap:12}}>
                      <span style={{fontSize:12, fontWeight:700, color:'#6366f1', background:'rgba(99,102,241,.1)', padding:'4px 12px', borderRadius:100}}>{section.article}</span>
                      <span style={{fontSize:16, fontWeight:600, color:'#0f172a'}}>{section.title}</span>
                    </div>
                    <span style={{fontSize:20, color: isOpen ? '#6366f1' : '#94a3b8', transform: isOpen ? 'rotate(45deg)' : 'none', transition:'transform .2s', flexShrink:0}}>+</span>
                  </button>
                  {isOpen && (
                    <div style={{padding:'0 24px 24px'}}>
                      <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
                      <table style={{width:'100%', borderCollapse:'collapse', minWidth:480}}>
                        <thead>
                          <tr>
                            {['CRA Requirement', 'DepGuard Coverage', ''].map(h => (
                              <th key={h} style={{textAlign:'left', padding:'10px 12px', fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:.5, borderBottom:'1px solid #f1f5f9'}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.items.map((item, ii) => (
                            <tr key={ii} style={{background: ii % 2 === 0 ? '#f8fafc' : '#fff'}}>
                              <td style={{padding:'14px 12px', fontSize:13, color:'#334155', lineHeight:1.5, borderBottom:'1px solid #f1f5f9', width:'40%'}}>{item.req}</td>
                              <td style={{padding:'14px 12px', fontSize:13, color:'#475569', lineHeight:1.5, borderBottom:'1px solid #f1f5f9'}}>{item.depguard}</td>
                              <td style={{padding:'14px 12px', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap'}}><CoveredChip covered={item.covered} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SBOM fields */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:860, margin:'0 auto'}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag">SBOM requirements</span>
            <h2>Every field CRA Article 20 mandates.</h2>
            <p className="dgw-section-lead">DepGuard's SBOM quality checker validates all 7 mandatory fields and flags missing data before your auditor does.</p>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'separate', borderSpacing:0}}>
              <thead>
                <tr>
                  {['SBOM Field', 'CRA Mandatory?', 'Example value'].map(h => (
                    <th key={h} style={{textAlign:'left', padding:'12px 16px', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:.5, background:'#f8fafc', borderBottom:'2px solid #e2e8f0'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SBOM_FIELDS.map((f, i) => (
                  <tr key={f.field} style={{background: i % 2 === 0 ? '#fff' : '#f8fafc'}}>
                    <td style={{padding:'14px 16px', fontSize:14, fontWeight:600, color:'#0f172a', borderBottom:'1px solid #f1f5f9'}}>{f.field}</td>
                    <td style={{padding:'14px 16px', borderBottom:'1px solid #f1f5f9'}}>
                      {f.required
                        ? <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'rgba(239,68,68,.1)',color:'#991b1b',border:'1px solid rgba(239,68,68,.2)'}}>Required</span>
                        : <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'rgba(148,163,184,.1)',color:'#475569',border:'1px solid #e2e8f0'}}>Recommended</span>
                      }
                    </td>
                    <td style={{padding:'14px 16px', fontSize:13, color:'#64748b', fontFamily:'monospace', borderBottom:'1px solid #f1f5f9'}}>{f.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="dgw-cta-band">
        <div className="dgw-cta-band-inner">
          <h2>Get your first CRA compliance report in 60 seconds.</h2>
          <p>Upload any SBOM. DepGuard validates all 7 mandatory Article 20 fields, calculates your compliance percentage, and generates a downloadable audit document. Try it before your coffee gets cold.</p>
          <div className="dgw-cta-band-actions">
            <Link to="/trial" className="dgw-btn-cta-white">Start free →</Link>
            <Link to="/website/whitepaper" className="dgw-btn-cta-ghost">Read the whitepaper</Link>
            <Link to="/website/contact" className="dgw-btn-cta-ghost">Talk to an expert</Link>
          </div>
        </div>
      </section>

    </div>
  );
}
